import Stomp from 'stompjs';

// 全局 STOMP 默认配置：地址、鉴权、心跳、去重和重连策略等
const DEFAULT_CONFIG = {
	url: '', // 完整 ws/wss 地址，优先级高于 baseUrl + path
	urlProvider: null, // 动态返回完整 ws/wss 地址
	baseUrl: '', // 基础域名，一般是从 store 或当前 origin 获取
	baseUrlProvider: null, // 动态返回基础域名
	path: '/api/ws/event', // websocket 路径
	connectHeaders: {}, // 固定连接头
	headersProvider: null, // 动态连接头，常用于 token
	stompDebug: false, // 是否打开 stompjs 原生日志
	heartbeatOutgoingMs: 5000, // 客户端发送心跳间隔
	heartbeatIncomingMs: 5000, // 客户端期望接收心跳间隔
	messageDedupWindowMs: 250, // 同 topic 消息短时间去重窗口
	reconnect: {
		enabled: true, // 是否允许自动重连
		initialDelayMs: 1000, // 首次重连等待时间
		maxDelayMs: 30000, // 重连等待上限
		multiplier: 1.8, // 指数退避倍数
		jitterRatio: 0.2, // 延迟抖动比例，避免同时打爆服务端
		maxAttempts: Infinity // 最大重连次数
	},
	logger: null, // 自定义日志输出
	onStateChange: null, // 状态变化回调
	onError: null // 统一错误回调
};

// 判断一个值是否为函数
function isFn(value) {
	return typeof value === 'function';
}

// 判断一个值是否为普通对象
function isObject(value) {
	return value && typeof value === 'object' && !Array.isArray(value);
}

// 浅拷贝对象，非对象时返回空对象
function shallowClone(value) {
	if (!isObject(value)) {
		return {};
	}
	return Object.assign({}, value);
}

// 合并默认配置、当前配置和新配置
function mergeConfig(baseConfig, nextConfig) {
	const merged = Object.assign({}, DEFAULT_CONFIG, baseConfig || {}, nextConfig || {});
	merged.connectHeaders = Object.assign(
		{},
		DEFAULT_CONFIG.connectHeaders,
		shallowClone(baseConfig && baseConfig.connectHeaders),
		shallowClone(nextConfig && nextConfig.connectHeaders)
	);
	merged.reconnect = Object.assign(
		{},
		DEFAULT_CONFIG.reconnect,
		shallowClone(baseConfig && baseConfig.reconnect),
		shallowClone(nextConfig && nextConfig.reconnect)
	);
	return merged;
}

// 转成有限数字，失败时回退到默认值
function toFiniteNumber(value, fallback) {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
}

// 转成正数，失败时回退到默认值
function toPositiveNumber(value, fallback) {
	const numeric = Number(value);
	return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
}

// 递归排序对象键，保证序列化结果稳定
function sortSerializable(value) {
	if (Array.isArray(value)) {
		return value.map(sortSerializable);
	}
	if (!isObject(value)) {
		return value;
	}
	const result = {};
	Object.keys(value)
		.sort()
		.forEach((key) => {
			result[key] = sortSerializable(value[key]);
		});
	return result;
}

// 生成稳定字符串，用于构造去重 key
function stableSerialize(value) {
	return JSON.stringify(sortSerializable(value || {}));
}

// 获取当前时间戳
function now() {
	return Date.now();
}

// 拼接基础地址和路径
function joinUrl(baseUrl, path) {
	const safeBase = String(baseUrl || '').trim();
	const safePath = String(path || '').trim();

	if (!safeBase) {
		return safePath;
	}

	if (!safePath) {
		return safeBase;
	}

	if (/^(ws|wss|http|https):\/\//i.test(safePath)) {
		return safePath;
	}

	if (safeBase.charAt(safeBase.length - 1) === '/' && safePath.charAt(0) === '/') {
		return safeBase + safePath.slice(1);
	}

	if (safeBase.charAt(safeBase.length - 1) !== '/' && safePath.charAt(0) !== '/') {
		return safeBase + '/' + safePath;
	}

	return safeBase + safePath;
}

// 规范化 ws 地址，兼容 http/https 和相对路径
function normalizeWsUrl(inputUrl) {
	const rawUrl = String(inputUrl || '').trim();
	if (!rawUrl) {
		return '';
	}

	if (/^wss?:\/\//i.test(rawUrl)) {
		return rawUrl;
	}

	if (/^https?:\/\//i.test(rawUrl)) {
		return rawUrl.replace(/^http/i, 'ws');
	}

	if (/^\/\//.test(rawUrl)) {
		if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') {
			return 'wss:' + rawUrl;
		}
		return 'ws:' + rawUrl;
	}

	if (rawUrl.charAt(0) === '/' && typeof window !== 'undefined' && window.location) {
		return normalizeWsUrl(window.location.origin + rawUrl);
	}

	return rawUrl;
}

// 尝试解析 JSON 字符串，失败时保留原值
function safeJsonParse(body) {
	if (typeof body !== 'string') {
		return body;
	}

	const trimmed = body.trim();
	if (!trimmed) {
		return body;
	}

	const firstChar = trimmed.charAt(0);
	if (firstChar !== '{' && firstChar !== '[') {
		return body;
	}

	try {
		return JSON.parse(trimmed);
	} catch (error) {
		return body;
	}
}

// 统一把各种错误输入转换成 Error 对象
function normalizeError(error, fallbackMessage) {
	if (error instanceof Error) {
		return error;
	}

	if (typeof error === 'string') {
		return new Error(error);
	}

	if (error && error.message) {
		return new Error(error.message);
	}

	return new Error(fallbackMessage || 'Unknown STOMP error');
}

class GlobalStompService {
	// 初始化连接状态、订阅池和环境监听器
	constructor() {
		this.config = mergeConfig(DEFAULT_CONFIG);
		this.webSocket = null;
		this.stompClient = null;
		this.state = 'idle';
		this.connected = false;
		this.desiredConnected = false;
		this.connectionToken = 0;
		this.reconnectAttempt = 0;
		this.reconnectTimer = null;
		this.scopeRecords = Object.create(null);
		this.topicRecords = Object.create(null);
		this.listeners = {
			statechange: [],
			connected: [],
			disconnected: [],
			error: [],
			message: []
		};
		this.lastActivityAt = 0;
		this.lastConnectedAt = 0;
		this.lastMessageAt = 0;
		this.lastDisconnectAt = 0;
		this.lastDisconnectReason = '';
		this.lastError = null;
		this.lastErrorAt = 0;
		this.messageCount = 0;
		this.currentUrl = '';
		this.connectedFrameHeaders = {};

		this._handleOnline = this._handleOnline.bind(this);
		this._handleOffline = this._handleOffline.bind(this);
		this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
		this._handleBeforeUnload = this._handleBeforeUnload.bind(this);

		this._bindEnvironmentListeners();
	}

	// 合并新的全局配置，不会直接打断当前连接
	configure(nextConfig) {
		this.config = mergeConfig(this.config, nextConfig || {});
		return this;
	}

	// 注册内部事件监听器
	addListener(eventName, handler) {
		if (!eventName || !isFn(handler) || !this.listeners[eventName]) {
			return function noop() {};
		}

		if (this.listeners[eventName].indexOf(handler) === -1) {
			this.listeners[eventName].push(handler);
		}

		return () => {
			this.removeListener(eventName, handler);
		};
	}

	// 移除内部事件监听器
	removeListener(eventName, handler) {
		const bucket = this.listeners[eventName];
		if (!bucket || !bucket.length) {
			return;
		}

		const index = bucket.indexOf(handler);
		if (index !== -1) {
			bucket.splice(index, 1);
		}
	}

	// 兼容旧调用，内部转到 start
	connect(metadata) {
		return this.start(metadata);
	}

	// 标记“期望保持连接”，并在必要时发起一次连接
	start(metadata) {
		this.desiredConnected = true;
		this._ensureConnection(metadata && metadata.reason ? metadata.reason : 'start');
		return this;
	}

	// 主动停止全局连接，可选清空全部 scope 订阅
	stop(options) {
		const stopOptions = options || {};
		this.desiredConnected = false;
		this._clearReconnectTimer();
		this._invalidateConnectionToken();
		this.lastDisconnectAt = now();
		this.lastDisconnectReason = stopOptions.reason || 'stop';
		this._cleanupTransport({
			graceful: true,
			reason: stopOptions.reason || 'stop'
		});

		if (stopOptions.clearSubscriptions) {
			this._clearAllScopes();
		}

		this._setState('disconnected', {
			reason: stopOptions.reason || 'stop'
		});
		this._emit('disconnected', {
			reason: stopOptions.reason || 'stop',
			manual: true,
			state: this.getState()
		});
		return this;
	}

	// 确保某个业务 scope 已经注册
	ensureScope(scopeId, metadata) {
		return this._ensureScopeRecord(scopeId, metadata);
	}

	// 销毁某个业务 scope，并清理它的全部订阅
	destroyScope(scopeId) {
		const scopeRecord = this.scopeRecords[scopeId];
		if (!scopeRecord) {
			return false;
		}

		Object.keys(scopeRecord.subscriptions).forEach((subscriptionKey) => {
			this._removeLocalSubscription(scopeRecord.subscriptions[subscriptionKey]);
		});

		delete this.scopeRecords[scopeId];
		return true;
	}

	// 为某个业务 scope 注册订阅；同 scope 下相同 key 会被替换
	subscribe(scopeId, destination, handler, options) {
		if (!scopeId) {
			throw new Error('[GlobalStompService] scopeId is required');
		}
		if (!destination) {
			throw new Error('[GlobalStompService] subscribe destination is required');
		}
		if (!isFn(handler)) {
			throw new Error('[GlobalStompService] subscribe handler must be a function');
		}

		const subscribeOptions = options || {};
		const scopeRecord = this._ensureScopeRecord(scopeId, subscribeOptions.scopeMeta);
		const subscriptionKey = subscribeOptions.key || destination;
		const topicHeaders = shallowClone(subscribeOptions.headers);
		const topicRecord = this._ensureTopicRecord(destination, topicHeaders);
		const existing = scopeRecord.subscriptions[subscriptionKey];

		if (existing) {
			this._removeLocalSubscription(existing);
		}

		const localSubscription = {
			id: scopeId + '::' + subscriptionKey,
			scopeId: scopeId,
			key: subscriptionKey,
			destination: destination,
			topicKey: topicRecord.key,
			headers: topicHeaders,
			handler: handler,
			parseJsonMessage: subscribeOptions.parseJsonMessage !== false,
			debounceMs: toPositiveNumber(subscribeOptions.debounceMs, 0),
			throttleMs: toPositiveNumber(subscribeOptions.throttleMs, 0),
			throttleTrailing: subscribeOptions.throttleTrailing !== false,
			debounceTimer: null,
			throttleTimer: null,
			pendingEnvelope: null,
			lastDeliveredAt: 0
		};

		scopeRecord.subscriptions[subscriptionKey] = localSubscription;
		topicRecord.consumers[localSubscription.id] = localSubscription;

		if (this.connected) {
			this._ensureTopicSubscription(topicRecord);
		} else if (this.desiredConnected) {
			this._ensureConnection('subscribe');
		}

		return {
			scopeId: scopeId,
			key: subscriptionKey,
			destination: destination,
			unsubscribe: () => {
				this.unsubscribe(scopeId, subscriptionKey);
			}
		};
	}

	// 取消某个 scope 下的订阅，支持按 key 或 destination 删除
	unsubscribe(scopeId, keyOrDestination) {
		const scopeRecord = this.scopeRecords[scopeId];
		if (!scopeRecord) {
			return false;
		}

		let targetKey = keyOrDestination;
		if (!scopeRecord.subscriptions[targetKey]) {
			targetKey = Object.keys(scopeRecord.subscriptions).find((subscriptionKey) => {
				const subscription = scopeRecord.subscriptions[subscriptionKey];
				return subscription && subscription.destination === keyOrDestination;
			});
		}

		if (!targetKey || !scopeRecord.subscriptions[targetKey]) {
			return false;
		}

		this._removeLocalSubscription(scopeRecord.subscriptions[targetKey]);
		delete scopeRecord.subscriptions[targetKey];

		if (!Object.keys(scopeRecord.subscriptions).length) {
			delete this.scopeRecords[scopeId];
		}

		return true;
	}

	// 通过当前全局连接发送消息
	send(destination, body, headers) {
		if (!this.connected || !this.stompClient) {
			throw new Error('[GlobalStompService] STOMP client is not connected');
		}
		if (!destination) {
			throw new Error('[GlobalStompService] send destination is required');
		}

		let payload = body;
		if (payload === null || typeof payload === 'undefined') {
			payload = '';
		} else if (typeof payload !== 'string') {
			payload = JSON.stringify(payload);
		}

		this.stompClient.send(destination, shallowClone(headers), payload);
	}

	// 判断当前是否已建立 STOMP 连接
	isConnected() {
		return this.connected;
	}

	// 判断当前是否处于连接中或已连接状态
	isActive() {
		return this.connected || this.state === 'connecting' || this.state === 'reconnecting';
	}

	// 返回轻量级运行态，适合业务代码做状态判断
	getState() {
		const scopeCount = Object.keys(this.scopeRecords).length;
		let subscriptionCount = 0;
		Object.keys(this.scopeRecords).forEach((scopeId) => {
			subscriptionCount += Object.keys(this.scopeRecords[scopeId].subscriptions).length;
		});

		return {
			state: this.state,
			connected: this.connected,
			desiredConnected: this.desiredConnected,
			reconnectAttempt: this.reconnectAttempt,
			reconnectScheduled: Boolean(this.reconnectTimer),
			scopeCount: scopeCount,
			subscriptionCount: subscriptionCount,
			topicCount: Object.keys(this.topicRecords).length,
			lastActivityAt: this.lastActivityAt,
			lastConnectedAt: this.lastConnectedAt,
			lastMessageAt: this.lastMessageAt,
			lastDisconnectAt: this.lastDisconnectAt,
			lastDisconnectReason: this.lastDisconnectReason,
			currentUrl: this.currentUrl
		};
	}

	// 返回完整调试快照，开发态调试面板直接使用这里的数据
	getDebugSnapshot() {
		const scopes = Object.keys(this.scopeRecords)
			.sort()
			.map((scopeId) => {
				const scopeRecord = this.scopeRecords[scopeId];
				const subscriptions = Object.keys(scopeRecord.subscriptions)
					.sort()
					.map((subscriptionKey) => {
						const subscription = scopeRecord.subscriptions[subscriptionKey];
						return {
							id: subscription.id,
							key: subscription.key,
							destination: subscription.destination,
							debounceMs: subscription.debounceMs,
							throttleMs: subscription.throttleMs,
							lastDeliveredAt: subscription.lastDeliveredAt,
							parseJsonMessage: subscription.parseJsonMessage
						};
					});

				return {
					id: scopeRecord.id,
					metadata: shallowClone(scopeRecord.metadata),
					subscriptionCount: subscriptions.length,
					subscriptions: subscriptions
				};
			});

		const topics = Object.keys(this.topicRecords)
			.sort()
			.map((topicKey) => {
				const topicRecord = this.topicRecords[topicKey];
				const consumers = Object.keys(topicRecord.consumers)
					.sort()
					.map((consumerId) => {
						const consumer = topicRecord.consumers[consumerId];
						return {
							id: consumer.id,
							scopeId: consumer.scopeId,
							key: consumer.key
						};
					});

				return {
					key: topicRecord.key,
					destination: topicRecord.destination,
					headers: shallowClone(topicRecord.headers),
					consumerCount: consumers.length,
					consumers: consumers,
					subscribed: Boolean(topicRecord.subscription),
					lastMessageAt: topicRecord.lastMessageAt
				};
			});

		const socketReadyState = this.webSocket ? this.webSocket.readyState : null;

		return {
			state: this.getState(),
			transport: {
				currentUrl: this.currentUrl,
				socketReadyState: socketReadyState,
				socketReadyStateLabel: this._getSocketReadyStateLabel(socketReadyState),
				configuredHeartbeat: {
					outgoing: this.config.heartbeatOutgoingMs,
					incoming: this.config.heartbeatIncomingMs
				},
				brokerHeartbeat: this.connectedFrameHeaders['heart-beat'] || '',
				connectedFrameHeaders: shallowClone(this.connectedFrameHeaders)
			},
			metrics: {
				messageCount: this.messageCount,
				lastActivityAt: this.lastActivityAt,
				lastConnectedAt: this.lastConnectedAt,
				lastMessageAt: this.lastMessageAt,
				lastDisconnectAt: this.lastDisconnectAt,
				lastDisconnectReason: this.lastDisconnectReason,
				lastErrorAt: this.lastErrorAt
			},
			lastError: this.lastError ? Object.assign({}, this.lastError) : null,
			scopes: scopes,
			topics: topics
		};
	}

	// 浏览器恢复联网后，按需重新建立连接
	_handleOnline() {
		if (!this.desiredConnected) {
			return;
		}

		this._clearReconnectTimer();
		this._ensureConnection('online');
	}

	// 浏览器离线时，立刻清理当前连接状态
	_handleOffline() {
		this._clearReconnectTimer();
		this._invalidateConnectionToken();
		this._cleanupTransport({
			graceful: false,
			reason: 'offline'
		});
		this._setState('paused-offline', {
			reason: 'offline'
		});
	}

	// 页面重新可见时，补一次连接检查
	_handleVisibilityChange() {
		if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
			return;
		}

		if (this.desiredConnected && !this.isActive()) {
			this._ensureConnection('visible');
		}
	}

	// 页面卸载前主动断开，避免残留连接
	_handleBeforeUnload() {
		this.stop({
			reason: 'beforeunload',
			clearSubscriptions: false
		});
	}

	// 绑定浏览器在线状态和页面生命周期事件
	_bindEnvironmentListeners() {
		if (typeof window !== 'undefined' && window.addEventListener) {
			window.addEventListener('online', this._handleOnline);
			window.addEventListener('offline', this._handleOffline);
			window.addEventListener('beforeunload', this._handleBeforeUnload);
		}

		if (typeof document !== 'undefined' && document.addEventListener) {
			document.addEventListener('visibilitychange', this._handleVisibilityChange);
		}
	}

	// 根据当前配置和状态，确保底层只存在一条有效连接
	_ensureConnection(reason) {
		if (!this.desiredConnected) {
			return;
		}

		if (this.connected || this.state === 'connecting') {
			return;
		}

		if (!this._isOnline()) {
			this._setState('paused-offline', {
				reason: reason || 'offline'
			});
			return;
		}

		const wsUrl = this._resolveWsUrl();
		if (!wsUrl) {
			this._emitError('invalid-url', new Error('WebSocket URL is empty'), {
				reason: reason || 'invalid-url'
			});
			return;
		}

		this._clearReconnectTimer();
		this._cleanupTransport({
			graceful: false,
			reason: 'reconnect-prepare'
		});

		const token = ++this.connectionToken;
		const socket = new WebSocket(wsUrl);
		const client = Stomp.over(socket);

		this.webSocket = socket;
		this.stompClient = client;
		this.currentUrl = wsUrl;
		this.connected = false;
		this._setState('connecting', {
			reason: reason || 'connect',
			url: wsUrl,
			attempt: this.reconnectAttempt
		});

		this._bindTransportEvents(socket, token);

		if (!this.config.stompDebug) {
			client.debug = null;
		} else {
			client.debug = (message) => {
				this._log(message);
			};
		}

		client.heartbeat.outgoing = toPositiveNumber(
			this.config.heartbeatOutgoingMs,
			DEFAULT_CONFIG.heartbeatOutgoingMs
		);
		client.heartbeat.incoming = toPositiveNumber(
			this.config.heartbeatIncomingMs,
			DEFAULT_CONFIG.heartbeatIncomingMs
		);

		client.onStompError = (frame) => {
			if (token !== this.connectionToken) {
				return;
			}

			const message = frame && frame.headers && frame.headers.message ? frame.headers.message : 'STOMP error frame';
			this._emitError('stomp-frame-error', new Error(message), {
				frame: frame,
				state: this.getState()
			});
		};

		const headers = this._buildConnectHeaders();

		try {
			client.connect(
				headers,
				(frame) => {
					this._handleConnected(token, frame, headers);
				},
				(error) => {
					this._handleConnectFailure(token, 'connect-error', error, {
						headers: headers,
						url: wsUrl
					});
				}
			);
		} catch (error) {
			this._handleConnectFailure(token, 'connect-throw', error, {
				headers: headers,
				url: wsUrl
			});
		}
	}

	// 绑定底层 WebSocket 事件，更新活动时间并接管异常
	_bindTransportEvents(socket, token) {
		if (!socket || !socket.addEventListener) {
			return;
		}

		socket.addEventListener('open', () => {
			if (token !== this.connectionToken) {
				return;
			}
			this.lastActivityAt = now();
		});

		socket.addEventListener('message', () => {
			if (token !== this.connectionToken) {
				return;
			}
			this.lastActivityAt = now();
		});

		socket.addEventListener('error', (event) => {
			if (token !== this.connectionToken) {
				return;
			}
			this._emitError('transport-error', normalizeError(event, 'WebSocket transport error'), {
				event: event,
				state: this.getState()
			});
		});

		socket.addEventListener('close', (event) => {
			this._handleConnectFailure(token, 'transport-close', event, {
				event: event
			});
		});
	}

	// 连接成功后刷新状态，并重建全部 topic 订阅
	_handleConnected(token, frame, headers) {
		if (token !== this.connectionToken) {
			return;
		}

		this.connected = true;
		this.reconnectAttempt = 0;
		this.lastConnectedAt = now();
		this.lastActivityAt = this.lastConnectedAt;
		this.connectedFrameHeaders = shallowClone(frame && frame.headers);
		this._setState('connected', {
			reason: 'connected'
		});
		this._resubscribeAllTopics();
		this._emit('connected', {
			frame: frame,
			headers: headers,
			state: this.getState()
		});
	}

	// 统一处理连接失败或 socket 关闭，并决定是否进入重连
	_handleConnectFailure(token, type, rawError, extra) {
		if (token !== this.connectionToken) {
			return;
		}

		const error = normalizeError(rawError, type);
		const wasConnected = this.connected;
		this.lastDisconnectAt = now();
		this.lastDisconnectReason = type;

		this._invalidateConnectionToken();
		this.connected = false;
		this._cleanupTransport({
			graceful: false,
			reason: type
		});

		this._emitError(type, error, Object.assign({}, extra, {
			state: this.getState()
		}));

		if (wasConnected) {
			this._emit('disconnected', {
				reason: type,
				error: error,
				state: this.getState()
			});
		}

		if (this.desiredConnected && this.config.reconnect.enabled && this._isOnline()) {
			this._scheduleReconnect(type, error);
			return;
		}

		this._setState(this._isOnline() ? 'disconnected' : 'paused-offline', {
			reason: type,
			error: error.message
		});
	}

	// 按指数退避策略安排下一次重连
	_scheduleReconnect(reason, error) {
		if (this.reconnectTimer) {
			return;
		}

		const reconnectConfig = this.config.reconnect || DEFAULT_CONFIG.reconnect;
		const nextAttempt = this.reconnectAttempt + 1;
		if (Number.isFinite(reconnectConfig.maxAttempts) && nextAttempt > reconnectConfig.maxAttempts) {
			this._setState('disconnected', {
				reason: 'reconnect-exhausted',
				error: error ? error.message : ''
			});
			return;
		}

		this.reconnectAttempt = nextAttempt;
		const baseDelay = toPositiveNumber(reconnectConfig.initialDelayMs, DEFAULT_CONFIG.reconnect.initialDelayMs);
		const maxDelay = toPositiveNumber(reconnectConfig.maxDelayMs, DEFAULT_CONFIG.reconnect.maxDelayMs);
		const multiplier = toPositiveNumber(reconnectConfig.multiplier, DEFAULT_CONFIG.reconnect.multiplier);
		const jitterRatio = Math.max(0, toFiniteNumber(reconnectConfig.jitterRatio, DEFAULT_CONFIG.reconnect.jitterRatio));
		const rawDelay = Math.min(maxDelay, baseDelay * Math.pow(multiplier, Math.max(0, nextAttempt - 1)));
		const jitter = rawDelay * jitterRatio * Math.random();
		const delay = Math.round(rawDelay + jitter);

		this._setState('reconnecting', {
			reason: reason,
			attempt: nextAttempt,
			delay: delay
		});

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			this._ensureConnection('reconnect');
		}, delay);
	}

	// 清除待执行的重连定时器
	_clearReconnectTimer() {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}

	// 清理当前 transport，并把 topic 的底层订阅句柄重置掉
	_cleanupTransport(options) {
		const cleanupOptions = options || {};
		const client = this.stompClient;
		const socket = this.webSocket;

		this.stompClient = null;
		this.webSocket = null;
		this.connected = false;

		Object.keys(this.topicRecords).forEach((topicKey) => {
			this.topicRecords[topicKey].subscription = null;
		});

		if (client) {
			if (cleanupOptions.graceful && isFn(client.disconnect)) {
				try {
					client.disconnect(function noop() {});
				} catch (error) {
					this._log('disconnect error: ' + error.message);
				}
			}

			if (isFn(client._cleanUp)) {
				try {
					client._cleanUp();
				} catch (error) {
					this._log('heartbeat cleanup error: ' + error.message);
				}
			}

			client.onStompError = null;
			client.debug = null;
		}

		if (socket && cleanupOptions.graceful) {
			try {
				if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
					socket.close();
				}
			} catch (error) {
				this._log('socket close error: ' + error.message);
			}
		}
	}

	// 连接恢复后，重新订阅当前全部 topic
	_resubscribeAllTopics() {
		Object.keys(this.topicRecords).forEach((topicKey) => {
			this._ensureTopicSubscription(this.topicRecords[topicKey]);
		});
	}

	// 把一个逻辑 topic 映射成真实的 STOMP SUBSCRIBE
	_ensureTopicSubscription(topicRecord) {
		if (!topicRecord || !this.connected || !this.stompClient || topicRecord.subscription) {
			return;
		}

		if (!Object.keys(topicRecord.consumers).length) {
			return;
		}

		try {
			const subscribeHeaders = this._buildSubscribeHeaders(topicRecord.headers);
			topicRecord.subscription = this.stompClient.subscribe(
				topicRecord.destination,
				(message) => {
					this._dispatchTopicMessage(topicRecord.key, message);
				},
				subscribeHeaders
			);
		} catch (error) {
			this._emitError('topic-subscribe-error', normalizeError(error, 'topic subscribe failed'), {
				destination: topicRecord.destination,
				state: this.getState()
			});
		}
	}

	// 同一个 topic 只收一次消息，再扇出给多个本地 consumer
	_dispatchTopicMessage(topicKey, message) {
		const topicRecord = this.topicRecords[topicKey];
		if (!topicRecord) {
			return;
		}

		const receivedAt = now();
		const signature = this._buildMessageSignature(topicRecord, message);
		const dedupWindowMs = toPositiveNumber(this.config.messageDedupWindowMs, DEFAULT_CONFIG.messageDedupWindowMs);

		if (
			signature &&
			topicRecord.lastMessageSignature === signature &&
			receivedAt - topicRecord.lastMessageAt <= dedupWindowMs
		) {
			return;
		}

		topicRecord.lastMessageSignature = signature;
		topicRecord.lastMessageAt = receivedAt;
		this.lastActivityAt = receivedAt;
		this.lastMessageAt = receivedAt;
		this.messageCount += 1;

		Object.keys(topicRecord.consumers).forEach((consumerId) => {
			const localSubscription = topicRecord.consumers[consumerId];
			if (!localSubscription) {
				return;
			}

			const envelope = this._buildEnvelope(localSubscription, topicRecord, message, receivedAt);
			this._dispatchToConsumer(localSubscription, envelope);
		});

		this._emit('message', {
			destination: topicRecord.destination,
			receivedAt: receivedAt,
			consumerCount: Object.keys(topicRecord.consumers).length,
			headers: shallowClone(message && message.headers),
			state: this.getState()
		});
	}

	// 构造传给业务回调的消息包
	_buildEnvelope(localSubscription, topicRecord, message, receivedAt) {
		const rawBody = message && Object.prototype.hasOwnProperty.call(message, 'body') ? message.body : '';
		return {
			body: localSubscription.parseJsonMessage ? safeJsonParse(rawBody) : rawBody,
			rawBody: rawBody,
			headers: shallowClone(message && message.headers),
			destination: topicRecord.destination,
			receivedAt: receivedAt,
			scopeId: localSubscription.scopeId,
			key: localSubscription.key,
			rawMessage: message
		};
	}

	// 根据防抖和节流配置选择投递方式
	_dispatchToConsumer(localSubscription, envelope) {
		if (localSubscription.debounceMs > 0) {
			this._queueDebouncedDelivery(localSubscription, envelope);
			return;
		}

		if (localSubscription.throttleMs > 0) {
			this._queueThrottledDelivery(localSubscription, envelope);
			return;
		}

		this._deliverToConsumer(localSubscription, envelope);
	}

	// 用防抖方式延后派发消息
	_queueDebouncedDelivery(localSubscription, envelope) {
		localSubscription.pendingEnvelope = envelope;
		if (localSubscription.debounceTimer) {
			clearTimeout(localSubscription.debounceTimer);
		}

		localSubscription.debounceTimer = setTimeout(() => {
			const pendingEnvelope = localSubscription.pendingEnvelope;
			localSubscription.debounceTimer = null;
			localSubscription.pendingEnvelope = null;
			if (pendingEnvelope) {
				this._deliverToConsumer(localSubscription, pendingEnvelope);
			}
		}, localSubscription.debounceMs);
	}

	// 用节流方式限制消息派发频率
	_queueThrottledDelivery(localSubscription, envelope) {
		const currentTime = now();
		const elapsed = currentTime - localSubscription.lastDeliveredAt;

		if (!localSubscription.lastDeliveredAt || elapsed >= localSubscription.throttleMs) {
			this._deliverToConsumer(localSubscription, envelope);
			return;
		}

		if (!localSubscription.throttleTrailing) {
			return;
		}

		localSubscription.pendingEnvelope = envelope;
		if (localSubscription.throttleTimer) {
			return;
		}

		localSubscription.throttleTimer = setTimeout(() => {
			const pendingEnvelope = localSubscription.pendingEnvelope;
			localSubscription.throttleTimer = null;
			localSubscription.pendingEnvelope = null;
			if (pendingEnvelope) {
				this._deliverToConsumer(localSubscription, pendingEnvelope);
			}
		}, Math.max(0, localSubscription.throttleMs - elapsed));
	}

	// 实际执行业务回调，并统一捕获异常
	_deliverToConsumer(localSubscription, envelope) {
		localSubscription.lastDeliveredAt = now();
		try {
			localSubscription.handler(envelope);
		} catch (error) {
			this._emitError('consumer-error', normalizeError(error, 'consumer handler failed'), {
				scopeId: localSubscription.scopeId,
				key: localSubscription.key,
				destination: localSubscription.destination
			});
		}
	}

	// 移除本地订阅，并在必要时取消底层 topic 订阅
	_removeLocalSubscription(localSubscription) {
		if (!localSubscription) {
			return;
		}

		if (localSubscription.debounceTimer) {
			clearTimeout(localSubscription.debounceTimer);
			localSubscription.debounceTimer = null;
		}

		if (localSubscription.throttleTimer) {
			clearTimeout(localSubscription.throttleTimer);
			localSubscription.throttleTimer = null;
		}

		localSubscription.pendingEnvelope = null;

		const topicRecord = this.topicRecords[localSubscription.topicKey];
		if (!topicRecord) {
			return;
		}

		delete topicRecord.consumers[localSubscription.id];

		if (!Object.keys(topicRecord.consumers).length) {
			if (topicRecord.subscription && isFn(topicRecord.subscription.unsubscribe)) {
				try {
					topicRecord.subscription.unsubscribe();
				} catch (error) {
					this._log('topic unsubscribe error: ' + error.message);
				}
			}
			delete this.topicRecords[localSubscription.topicKey];
		}
	}

	// 清空全部 scope 及其订阅
	_clearAllScopes() {
		Object.keys(this.scopeRecords).forEach((scopeId) => {
			this.destroyScope(scopeId);
		});
	}

	// 获取或创建 scope 记录
	_ensureScopeRecord(scopeId, metadata) {
		if (!this.scopeRecords[scopeId]) {
			this.scopeRecords[scopeId] = {
				id: scopeId,
				metadata: shallowClone(metadata),
				subscriptions: Object.create(null)
			};
		} else if (metadata) {
			this.scopeRecords[scopeId].metadata = Object.assign(
				{},
				this.scopeRecords[scopeId].metadata,
				metadata
			);
		}

		return this.scopeRecords[scopeId];
	}

	// 获取或创建 topic 记录，作为底层订阅的归并单元
	_ensureTopicRecord(destination, headers) {
		const normalizedHeaders = shallowClone(headers);
		const topicKey = destination + '::' + stableSerialize(normalizedHeaders);

		if (!this.topicRecords[topicKey]) {
			this.topicRecords[topicKey] = {
				key: topicKey,
				destination: destination,
				headers: normalizedHeaders,
				consumers: Object.create(null),
				subscription: null,
				lastMessageSignature: '',
				lastMessageAt: 0
			};
		}

		return this.topicRecords[topicKey];
	}

	// 生成本次 connect 使用的 headers
	_buildConnectHeaders() {
		const headers = Object.assign({}, this.config.connectHeaders || {});
		if (!isFn(this.config.headersProvider)) {
			return headers;
		}

		try {
			return Object.assign(headers, this.config.headersProvider() || {});
		} catch (error) {
			this._emitError('headers-provider-error', normalizeError(error, 'headers provider failed'), {
				state: this.getState()
			});
			return headers;
		}
	}

	// 解析最终要连接的 WebSocket 地址
	_resolveWsUrl() {
		let explicitUrl = this.config.url;
		if (isFn(this.config.urlProvider)) {
			try {
				explicitUrl = this.config.urlProvider();
			} catch (error) {
				this._emitError('url-provider-error', normalizeError(error, 'url provider failed'), {});
			}
		}

		if (explicitUrl) {
			return normalizeWsUrl(explicitUrl);
		}

		let baseUrl = this.config.baseUrl;
		if (isFn(this.config.baseUrlProvider)) {
			try {
				baseUrl = this.config.baseUrlProvider();
			} catch (error) {
				this._emitError('base-url-provider-error', normalizeError(error, 'base url provider failed'), {});
			}
		}

		return normalizeWsUrl(joinUrl(baseUrl, this.config.path));
	}

	// 生成消息签名，用于短时间重复消息过滤
	_buildMessageSignature(topicRecord, message) {
		const headers = message && message.headers ? message.headers : {};
		if (headers['message-id']) {
			return topicRecord.key + '::' + headers['message-id'];
		}

		const rawBody = message && typeof message.body === 'string' ? message.body : '';
		if (!rawBody) {
			return topicRecord.key + '::empty';
		}

		const normalizedBody = rawBody.length > 512 ? rawBody.slice(0, 512) + '::' + rawBody.length : rawBody;
		return topicRecord.key + '::' + normalizedBody;
	}

	// 每次底层订阅都复制一份 headers，避免 stompjs 回写 id 污染缓存
	_buildSubscribeHeaders(headers) {
		const nextHeaders = shallowClone(headers);
		delete nextHeaders.id;
		delete nextHeaders.destination;
		return nextHeaders;
	}

	// 判断当前网络是否在线
	_isOnline() {
		return typeof navigator === 'undefined' || navigator.onLine !== false;
	}

	// 让旧连接相关回调全部失效
	_invalidateConnectionToken() {
		this.connectionToken += 1;
	}

	// 更新内部状态，并对外广播状态变化
	_setState(nextState, context) {
		if (this.state === nextState) {
			return;
		}

		const previousState = this.state;
		this.state = nextState;
		const payload = {
			previousState: previousState,
			nextState: nextState,
			connected: this.connected,
			desiredConnected: this.desiredConnected,
			reconnectAttempt: this.reconnectAttempt,
			context: context || null,
			state: this.getState()
		};

		this._emit('statechange', payload);
		if (isFn(this.config.onStateChange)) {
			try {
				this.config.onStateChange(payload);
			} catch (error) {
				this._log('onStateChange callback error: ' + error.message);
			}
		}
	}

	// 触发指定事件的所有监听器
	_emit(eventName, payload) {
		const bucket = this.listeners[eventName];
		if (!bucket || !bucket.length) {
			return;
		}

		bucket.slice().forEach((handler) => {
			try {
				handler(payload);
			} catch (error) {
				this._log('listener error: ' + error.message);
			}
		});
	}

	// 统一记录并派发错误信息
	_emitError(type, error, extra) {
		this.lastErrorAt = now();
		this.lastError = {
			type: type,
			message: error.message,
			at: this.lastErrorAt
		};
		const payload = Object.assign(
			{
				type: type,
				error: error,
				message: error.message,
				state: this.getState()
			},
			extra || {}
		);

		this._emit('error', payload);
		if (isFn(this.config.onError)) {
			try {
				this.config.onError(payload);
			} catch (callbackError) {
				this._log('onError callback error: ' + callbackError.message);
			}
		}
		this._log(type + ': ' + error.message);
	}

	// 把 readyState 转成可读文本
	_getSocketReadyStateLabel(readyState) {
		if (readyState === WebSocket.CONNECTING) {
			return 'CONNECTING';
		}
		if (readyState === WebSocket.OPEN) {
			return 'OPEN';
		}
		if (readyState === WebSocket.CLOSING) {
			return 'CLOSING';
		}
		if (readyState === WebSocket.CLOSED) {
			return 'CLOSED';
		}
		return 'UNKNOWN';
	}

	// 统一日志出口，优先走外部 logger
	_log(message) {
		if (isFn(this.config.logger)) {
			try {
				this.config.logger(message, this.getState());
				return;
			} catch (error) {
				console.error('[GlobalStompService] logger failed:', error);
			}
		}

		if (typeof console !== 'undefined' && console.log) {
			console.log('[GlobalStompService]', message);
		}
	}
}

const globalStompService = new GlobalStompService();

export { GlobalStompService, globalStompService };
export default globalStompService;